import api from '@/factories/api';
import { compareStrings } from '@/filters/helpers';

export function isSystemRole(role) {
  return (
    role?.type === 'system' || String(role?.id || '').startsWith('system_')
  );
}

export default {
  namespaced: true,

  state: {
    roles: [],
    loaded: false,
    search: '',
  },

  mutations: {
    setRoles(state, roles) {
      state.roles = roles;
      state.loaded = true;
    },
    setSearch(state, search) {
      state.search = search;
    },
    addRole(state, role) {
      state.roles.push(role);
    },
    updateRole(state, updated) {
      const idx = state.roles.findIndex((r) => r.id === updated.id);
      if (idx !== -1) {
        state.roles.splice(idx, 1, updated);
      }
    },
    removeRole(state, id) {
      state.roles = state.roles.filter((r) => r.id !== id);
    },
  },

  actions: {
    async loadRoles({ commit, state }, { force = false } = {}) {
      if (state.loaded && !force) return;
      const roles = await api().get('roles');
      commit('setRoles', Array.isArray(roles) ? roles : []);
    },

    async createRole({ commit }, payload) {
      const role = await api().post('roles', payload);
      commit('addRole', role);
      return role;
    },

    async updateRole({ commit }, { id, ...payload }) {
      const role = await api().put(`roles/${id}`, payload);
      commit('updateRole', role);
      return role;
    },

    async deleteRole({ commit }, id) {
      await api().delete(`roles/${id}`);
      commit('removeRole', id);
    },

    async forceReload({ commit }) {
      const roles = await api().get('roles');
      commit('setRoles', Array.isArray(roles) ? roles : []);
    },
  },

  getters: {
    roles: (state) => state.roles,
    loaded: (state) => state.loaded,
    search: (state) => state.search,
    systemRoles: (state) => state.roles.filter((role) => isSystemRole(role)),
    customRoles: (state) => state.roles.filter((role) => !isSystemRole(role)),
    filteredRoles(state, getters) {
      if (!getters.search) {
        return getters.sortedRoles;
      }

      return getters.sortedRoles.filter((role) => {
        const haystack = [role.name, role.slug, role.description]
          .filter(Boolean)
          .join(' ');

        return compareStrings(haystack, getters.search);
      });
    },
    sortedRoles(state) {
      return [...state.roles].sort((a, b) => {
        const aSystem = isSystemRole(a);
        const bSystem = isSystemRole(b);

        if (aSystem !== bSystem) {
          return aSystem ? -1 : 1;
        }

        return (a.name || '').localeCompare(b.name || '');
      });
    },
    roleById: (state) => (id) => state.roles.find((r) => r.id === id),
  },
};
